import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { getCandidateFeedback } from '../api/feedback';
import BrandLogo from '../components/ui/BrandLogo';
import { getFitVerdictChip } from '../utils/formatters';
import FeedbackBody from '../candidate/components/FeedbackBody';

export default function CandidateFeedbackPage() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError('');
    getCandidateFeedback(token)
      .then((res) => {
        if (!cancelled) setData(res.data);
      })
      .catch((err) => {
        if (cancelled) return;
        const status = err.response?.status;
        const code = err.response?.data?.code;
        if (status === 410 || code === 'FEEDBACK_EXPIRED') {
          setError('This feedback link has expired. Feedback links remain active for 7 days following an interview.');
        } else {
          setError('We could not retrieve feedback for this link. It may be invalid or no longer active.');
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [token]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[var(--canvas)] flex items-center justify-center p-4">
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-[var(--teal-500)] border-t-transparent rounded-full animate-spin mx-auto mb-3" />
          <p className="text-xs text-[var(--ink-soft)]">Loading your feedback…</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[var(--canvas)] flex items-center justify-center p-4">
        <div className="card max-w-md w-full p-8 text-center shadow-card">
          <div className="w-12 h-12 rounded-full bg-[var(--rose-100)] text-[var(--rose-600)] flex items-center justify-center mx-auto mb-4">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-6 h-6">
              <circle cx="12" cy="12" r="10" />
              <line x1="12" y1="8" x2="12" y2="12" />
              <line x1="12" y1="16" x2="12.01" y2="16" />
            </svg>
          </div>
          <h1
            className="text-lg font-semibold text-[var(--navy-950)] mb-2"
            style={{ fontFamily: 'var(--font-heading)' }}
          >
            Feedback Unavailable
          </h1>
          <p className="text-xs text-[var(--ink-soft)] leading-relaxed mb-6">
            {error}
          </p>
          <Link to="/login" className="btn btn-ghost !text-xs">
            Return to Samastix
          </Link>
        </div>
      </div>
    );
  }

  const feedback = data?.feedback ?? {};
  const practice = feedback.mode === 'PRACTICE';
  // No default verdict: a chip only when the organization's feedback actually carries one.
  const chip = !practice && feedback.overallVerdict ? getFitVerdictChip(feedback.overallVerdict) : null;
  // The server already removes placeholders, e-mail headers and integrity rows (legacy rows
  // included). Nothing below invents strengths or focus areas.
  const view = { ...feedback, orgName: feedback.orgName || data?.orgName, feedbackText: feedback.emailBody };

  return (
    <div className="min-h-screen bg-[var(--canvas)] text-[var(--ink)] py-12 px-4 sm:px-6">
      <div className="max-w-xl mx-auto space-y-6">
        {/* Brand header */}
        <div className="flex items-center justify-between">
          <BrandLogo subtitle="CANDIDATE FEEDBACK" />
          {data?.orgName && (
            <span className="text-xs font-semibold text-[var(--ink-soft)] bg-white px-3 py-1 rounded-full border border-[var(--line)]">
              {data.orgName}
            </span>
          )}
        </div>

        {/* Main Feedback Card */}
        <div className="card p-6 sm:p-8 space-y-6 shadow-card">
          <div className="flex items-start justify-between gap-3 border-b border-[var(--line-soft)] pb-4">
            <div>
              <p className="text-xs text-[var(--ink-faint)]">
                {data?.generatedAt ? new Date(data.generatedAt).toLocaleDateString() : ''}
              </p>
              <h1
                className="text-xl sm:text-2xl font-bold text-[var(--navy-950)] mt-0.5"
                style={{ fontFamily: 'var(--font-heading)' }}
              >
                {practice ? 'Interview Practice Complete' : 'Your interview feedback'}
              </h1>
            </div>

            {chip && (
              <span className={`chip ${chip.chipClass} !text-xs !py-1 !px-3.5`}>
                {chip.label}
              </span>
            )}
          </div>

          <FeedbackBody feedback={view} />
        </div>

        {/* Footer */}
        <div className="text-center text-xs text-[var(--ink-faint)] space-y-1">
          <p>Powered by Samastix · AI-assisted interview intelligence</p>
          <p className="text-[11px]">Proctoring and integrity verification is reviewed confidentially by hiring administrators.</p>
        </div>
      </div>
    </div>
  );
}
